import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { decodeTimeFromUrl, encodeTimeToUrl } from '../utils/urlUtils';
import { snapToQuarterHour, ceilToQuarterHour } from '../utils/timeUtils';

export interface UseScheduleUrlOptions {
  /** Default date to use if no URL parameter exists */
  defaultDate?: Date;
  /** Whether to update the URL immediately on mount if no date parameter exists */
  updateUrlOnMount?: boolean;
}

export interface UseScheduleUrlReturn {
  /** Current date/time from URL or default */
  currentTime: Date;
  /** Function to update the current time and URL */
  setCurrentTime: (date: Date) => void;
  /** Whether the current time was loaded from URL parameters */
  isFromUrl: boolean;
  /** Get a shareable URL for the current time */
  getShareableUrl: () => string;
}

/**
 * Custom hook for managing date/time parameters in schedule URLs
 * 
 * This hook:
 * - Reads the initial date from URL parameters on mount
 * - Updates the URL when the date changes
 * - Provides utilities for URL sharing
 * - Maintains consistency with quarter-hour snapping
 * 
 * @param options Configuration options
 * @returns Object with current time state and management functions
 */
export const useScheduleUrl = (options: UseScheduleUrlOptions = {}): UseScheduleUrlReturn => {
  const {
    defaultDate = new Date(),
    updateUrlOnMount = true,
  } = options;

  const [searchParams, setSearchParams] = useSearchParams();
  const [isFromUrl, setIsFromUrl] = useState(false);
  
  // Initialize state from URL parameter or default
  const [currentTime, setCurrentTimeState] = useState<Date>(() => {
    const dateParam = searchParams.get('date');
    const urlDate = decodeTimeFromUrl(dateParam);
    
    if (urlDate) {
      setIsFromUrl(true);
      return urlDate;
    }
    
    // Use provided default or current time, snapped to quarter hour
    return snapToQuarterHour(defaultDate);
  });

  // Update URL when component mounts if no date parameter exists and updateUrlOnMount is true
  useEffect(() => {
    const dateParam = searchParams.get('date');
    
    if (!dateParam && updateUrlOnMount) {
      // Add current time to URL without creating a new history entry
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('date', encodeTimeToUrl(currentTime));
      setSearchParams(newSearchParams, { replace: true });
    }
  }, []); // Empty dependency array - only run on mount

  // Function to update current time and URL
  const setCurrentTime = useCallback((date: Date) => {
    // Smart snapping: If the date is already on a quarter hour, use it as-is.
    // Otherwise, check if snapping would go before the desired time and use ceil instead
    const minutes = date.getMinutes();
    const isOnQuarterHour = minutes % 15 === 0;

    let snappedDate: Date;
    if (isOnQuarterHour) {
      // Already on a quarter hour boundary
      snappedDate = new Date(date);
      snappedDate.setSeconds(0, 0);
    } else {
      // Check if normal snapping would go backwards
      const normalSnap = snapToQuarterHour(date);
      if (normalSnap.getTime() < date.getTime()) {
        // Normal snap goes backwards, use ceil to ensure we don't go before the trip
        snappedDate = ceilToQuarterHour(date);
      } else {
        // Normal snap goes forward or stays the same, use it
        snappedDate = normalSnap;
      }
    }

    setCurrentTimeState(snappedDate);

    // Update URL with new date
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('date', encodeTimeToUrl(snappedDate));

    // Use replace: true to avoid creating excessive browser history entries
    // This allows users to share the URL at any time without navigation issues
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Get shareable URL for current time
  const getShareableUrl = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('date', encodeTimeToUrl(currentTime));
    return url.toString();
  }, [currentTime]);

  return {
    currentTime,
    setCurrentTime,
    isFromUrl,
    getShareableUrl,
  };
};